import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { LibraryBundleCard } from "@/components/bundles/LibraryBundleCard";
import { LibraryBundleModal } from "@/components/bundles/LibraryBundleModal";
import { Footer } from "@/components/layout/Footer";
import { Navbar } from "@/components/layout/Navbar";
import { useBundleList } from "@/hooks/useBundles";
import { useSEO } from "@/hooks/useSEO";
import { LibraryTutorialCard } from "@/components/tutorials/LibraryTutorialCard";
import { useAddFreeCourse } from "@/hooks/useCheckout";
import { useCourseList } from "@/hooks/useCourses";
import { isPermissionExpired, useCoursePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/lib/auth";
import { useAppStore } from "@/lib/store";
import type { BundleWithCourses, CartBundleItem } from "@/lib/schema";

const defaultInstructorImage =
  "https://cdn.prod.website-files.com/686229256a807206c1874545/687bcf08468fe4993282a9ec_Khusbu.avif";

export function SavedPage() {
  useSEO({
    title: "Saved",
    description:
      "Your saved BollyRhythm tutorials and bundles. Come back anytime to pick up where you left off.",
    canonical: "/saved",
    noindex: true,
  });

  const { user } = useAuth();
  const { data: courses, loading } = useCourseList();
  const { data: bundles, loading: bundleLoading } = useBundleList();
  const { data: permissions, refetch: refetchPermissions } = useCoursePermissions();
  const { execute: addFreeCourse } = useAddFreeCourse();
  const {
    savedItems,
    savedBundleItems,
    addToCart,
    cartItems,
    isBundleSaved,
    isSaved,
    setCartOpen,
    toggleSaved,
    toggleSavedBundle,
  } = useAppStore();
  const [addingFreeCourseId, setAddingFreeCourseId] = useState<string | null>(null);
  const [freeCourseError, setFreeCourseError] = useState<string | null>(null);
  const [freeCourseMessage, setFreeCourseMessage] = useState<string | null>(null);
  const [selectedBundleId, setSelectedBundleId] = useState<string | null>(null);

  const activePermissionIds = useMemo(() => {
    return new Set(
      (permissions ?? [])
        .filter((permission) => !isPermissionExpired(permission))
        .map((permission) => permission.course_id.id),
    );
  }, [permissions]);

  const savedCourses = useMemo(() => {
    if (!courses || savedItems.length === 0) return [];
    return courses.filter((course) => savedItems.includes(course.id));
  }, [courses, savedItems]);

  const savedBundles = useMemo(() => {
    if (!bundles || savedBundleItems.length === 0) return [];
    return bundles.filter((bundle) => savedBundleItems.includes(bundle.id));
  }, [bundles, savedBundleItems]);

  const bundleItems = useMemo(
    () => cartItems.filter((item): item is CartBundleItem => item.type === "bundle"),
    [cartItems],
  );
  const cartBundleIds = useMemo(
    () => new Set(bundleItems.map((item) => item.bundle_id)),
    [bundleItems],
  );

  const totalSavedItems = savedCourses.length + savedBundles.length;
  const selectedBundle = useMemo(
    () => (selectedBundleId && bundles ? bundles.find((bundle) => bundle.id === selectedBundleId) ?? null : null),
    [bundles, selectedBundleId],
  );

  const loginHref = `/app/auth/login?redirect=${encodeURIComponent("/saved")}`;

  const handleAddToCart = (course: (typeof savedCourses)[number]) => {
    addToCart({
      type: "course",
      course_id: course.id,
      name: course.name,
      price: course.price,
    });
    setCartOpen(true);
  };

  const handleAddFree = async (courseId: string) => {
    setFreeCourseError(null);
    setFreeCourseMessage(null);
    setAddingFreeCourseId(courseId);
    const result = await addFreeCourse(courseId);
    if (result.error) {
      setFreeCourseError(result.error);
      setAddingFreeCourseId(null);
      return;
    }
    await refetchPermissions();
    setFreeCourseMessage("Free course added to your dashboard.");
    setAddingFreeCourseId(null);
  };

  const handleAddBundleToCart = (bundle: BundleWithCourses) => {
    addToCart({
      type: "bundle",
      bundle_id: bundle.id,
      name: bundle.name,
      price: bundle.price,
      thumbnail_url: bundle.thumbnail_url ?? undefined,
      course_ids: bundle.courses.map((course) => course.id),
      course_count: bundle.courses.length,
    });
    setCartOpen(true);
  };

  const getBundleOwnedCourseCount = (bundle: BundleWithCourses) => {
    return bundle.courses.filter((course) => activePermissionIds.has(course.id)).length;
  };

  const isBundleOwned = (bundle: BundleWithCourses) => {
    return bundle.courses.length > 0 && bundle.courses.every((course) => activePermissionIds.has(course.id));
  };

  const hasBundleCartConflict = (bundle: BundleWithCourses) => {
    const bundleCourseIds = bundle.courses.map((course) => course.id);
    return bundleItems.some(
      (item) =>
        item.bundle_id !== bundle.id &&
        item.course_ids.some((courseId) => bundleCourseIds.includes(courseId)),
    );
  };

  return (
    <div className="page-wrapper landing-page relative">
      <Navbar />

      <main className="landing-page__main main-wrapper">
        <section className="container-large mx-auto mt-14 px-[var(--site-margin)] pb-16">
          <div className="mb-10">
            <p className="text-xs font-semibold tracking-[0.12em] text-neutral uppercase">
              Saved Tutorials & Bundles
            </p>
            <h1 className="mt-3 font-display text-[clamp(2rem,1.5714rem+2.1429vw,3.5rem)] leading-[1.1] tracking-[-0.02em] text-[#1f0a18]">
              Your shortlist, ready when you are
            </h1>
            <p className="mt-3 max-w-3xl text-neutral-dark">
              Revisit the routines and bundles you bookmarked, then jump back into the dance library
              whenever you want more options.
            </p>
            <p className="mt-3 text-sm text-neutral">
              {loading || bundleLoading
                ? "Loading..."
                : `Showing ${totalSavedItems} saved item${totalSavedItems === 1 ? "" : "s"}`}
            </p>
            {freeCourseMessage ? (
              <div className="mt-4 rounded-[1.1rem] bg-[#ecfff3] px-[1.1rem] py-[0.95rem] text-[0.95rem] font-semibold text-[#1f7a45]">
                {freeCourseMessage}
              </div>
            ) : null}
            {freeCourseError ? (
              <div className="mt-4 rounded-[1.1rem] bg-[#fff3f0] px-[1.1rem] py-[0.95rem] text-[0.95rem] font-semibold text-[#a3533d]">
                {freeCourseError}
              </div>
            ) : null}
          </div>
          {loading || bundleLoading ? (
            <div className="grid gap-x-6 gap-y-12 md:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[27rem] animate-pulse rounded-[1.75rem] bg-white shadow-[0_24px_55px_rgba(74,25,60,0.07)]"
                />
              ))}
            </div>
          ) : totalSavedItems === 0 ? (
            <div className="rounded-[1.5rem] border border-dashed border-[#d9c2cf] bg-white px-6 py-14 text-center text-[#5f5a5d]">
              You have not saved any tutorials or bundles yet. Explore the library to build your shortlist.
            </div>
          ) : (
            <div className="grid gap-x-6 gap-y-12 md:grid-cols-2 xl:grid-cols-3">
              {savedBundles.map((bundle) => {
                const isOwned = isBundleOwned(bundle);
                const saved = isBundleSaved(bundle.id);
                const inCart = cartBundleIds.has(bundle.id);
                const hasCartConflict = !inCart && hasBundleCartConflict(bundle);
                const ownedCourseCount = getBundleOwnedCourseCount(bundle);

                return (
                  <LibraryBundleCard
                    key={bundle.id}
                    bundle={bundle}
                    instructorImage={defaultInstructorImage}
                    isLoggedIn={Boolean(user)}
                    isOwned={isOwned}
                    isSaved={saved}
                    inCart={inCart}
                    hasCartConflict={hasCartConflict}
                    ownedCourseCount={ownedCourseCount}
                    loginHref={loginHref}
                    onAddToCart={handleAddBundleToCart}
                    onOpenPreview={() => setSelectedBundleId(bundle.id)}
                    onToggleSaved={() => toggleSavedBundle(bundle.id)}
                  />
                );
              })}

              {savedCourses.map((course) => {
                const isOwned = activePermissionIds.has(course.id);
                const saved = isSaved(course.id);
                const inCart = cartItems.some(
                  (item) =>
                    (item.type === "course" && item.course_id === course.id) ||
                    (item.type === "bundle" && item.course_ids.includes(course.id)),
                );
                const addingFree = addingFreeCourseId === course.id;

                return (
                  <LibraryTutorialCard
                    key={course.id}
                    course={course}
                    instructorImage={defaultInstructorImage}
                    isLoggedIn={Boolean(user)}
                    isOwned={isOwned}
                    isSaved={saved}
                    inCart={inCart}
                    addingFree={addingFree}
                    loginHref={loginHref}
                    onAddFree={handleAddFree}
                    onAddToCart={handleAddToCart}
                    onOpenPreview={() => {}}
                    onToggleSaved={() => toggleSaved(course.id)}
                  />
                );
              })}

              {/* Browse Library card */}
              <Link
                to="/dance-library"
                className="flex min-h-[20rem] flex-col items-center justify-center rounded-[1.5rem] border-2 border-dashed border-[#d9c2cf] bg-[#faf8f9] text-center transition-all duration-300 ease-in-out hover:border-[#c4a6b8] hover:bg-[#f7f3f6] hover:shadow-[0_8px_30px_rgba(74,25,60,0.06)] hover:-translate-y-0.5"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className="mb-3 h-8 w-8 text-[#b09aaa]">
                  <path d="M228,128a12,12,0,0,1-12,12H140v76a12,12,0,0,1-24,0V140H40a12,12,0,0,1,0-24h76V40a12,12,0,0,1,24,0v76h76A12,12,0,0,1,228,128Z" />
                </svg>
                <span className="text-[1.1rem] font-semibold text-[#762a5c]">Browse Library</span>
                <span className="mt-1 text-sm text-[#9a8a95]">Discover more tutorials and bundles</span>
              </Link>
            </div>
          )}
        </section>
      </main>

      <Footer />

      {selectedBundle ? (
        <LibraryBundleModal
          bundle={selectedBundle}
          instructorImage={defaultInstructorImage}
          isLoggedIn={Boolean(user)}
          isOwned={isBundleOwned(selectedBundle)}
          isSaved={isBundleSaved(selectedBundle.id)}
          inCart={cartBundleIds.has(selectedBundle.id)}
          hasCartConflict={!cartBundleIds.has(selectedBundle.id) && hasBundleCartConflict(selectedBundle)}
          ownedCourseIds={activePermissionIds}
          loginHref={loginHref}
          onAddToCart={handleAddBundleToCart}
          onClose={() => setSelectedBundleId(null)}
          onToggleSaved={() => toggleSavedBundle(selectedBundle.id)}
        />
      ) : null}
    </div>
  );
}
